import {signalStore, withState, withMethods, patchState, withComputed} from '@ngrx/signals';
import {rxMethod} from '@ngrx/signals/rxjs-interop';
import {pipe, switchMap, tap, catchError, of, debounceTime, forkJoin, mergeMap, timer, takeUntil, Subject, finalize} from 'rxjs';
import {inject, computed} from '@angular/core';
import {IItem} from '../../types/IItem';
import {AddonRequest, ItemPricingRequest, ItemsService, PricingItem} from '../services/items.service';
import {ISearchParams} from '../../types/ICart';
import {EventService} from '../services/event.service';
import {IPriceLevel} from '../../types/IPriceLevel';
import {AppStore} from './app.store';
import {IPrice} from '../../types/IPrice';
import {IAddonForItem} from '../../types/IAddonForItem';
import {NavigationService} from '../services/navigation.service';
import {ToastService} from '../services/toast.service';
import {weightedCost, weightedPrice} from '../utils/item.utils';
import {CartItemService} from '../services/cart-item.service';
import {PostHogService} from '../services/posthog.service';
import {ICompareItem} from '../../types/ICompareItem';
import {ISearchProgress, ISearchStep} from '../../types/ISearchProgress';
import {setError, setFulfilled, setIdle, setPending, withRequestStatus} from './features/with-request-status';

type ResultsState = {
  search_params: ISearchParams | null,

  results: IItem[];
  search_progress: ISearchProgress;

  pricing_data: Record<string, IPrice>;

  addons_data: Record<string, IAddonForItem>;

  compare_items: { good?: ICompareItem; better?: ICompareItem; best?: ICompareItem; };

  price_level: IPriceLevel | null;
  quantity: number;
};

export const ResultsStore = signalStore(
  {providedIn: 'root'},
  withState<ResultsState>({
    search_params: null,

    results: [],
    search_progress: {},

    pricing_data: {},

    addons_data: {},

    compare_items: {},

    price_level: null,
    quantity: 4
  }),
  withRequestStatus('search'),
  withRequestStatus('pricing'),
  withRequestStatus('addons'),
  withComputed((state, appStore = inject(AppStore)) => ({
    hasResults: computed(() => state.results().length > 0),

    searchSteps: computed((): ISearchStep[] => {
      const vendorNames = new Map(appStore.client_vendors().map(vendor => [vendor.key, vendor.name]));

      return Object.entries(state.search_progress()).map(([key, progress]) => ({
        key,
        label: key === 'local' ? 'Local Inventory' : (vendorNames.get(key) ?? key),
        ...progress,
      }));
    }),

    getPricing: computed(() => (itemId: string | number, cost: number) => {
      const priceLevelId = state.price_level()?.id ?? appStore.defaultPriceLevel()?.id;

      return state.pricing_data()[`${priceLevelId}_${itemId}_${cost}`] ?? null;
    }),

    isInCompare: computed(() => (item: IItem) => {
      const items = state.compare_items();
      return items.good?.id === item?.id || items.better?.id === item?.id || items.best?.id === item?.id;
    }),
    hasCompareItems: computed(() => state.compare_items().good || state.compare_items().better || state.compare_items().best),

    currentPriceLevel: computed(() => state.price_level() ?? appStore.defaultPriceLevel()),

    getAddonsForItem: computed(() => (itemId: number | string, cost?: number, quantity?: number): IAddonForItem | null => {
      const addonsData = state.addons_data();
      const priceLevel = state.price_level() ?? appStore.defaultPriceLevel();

      let key = `${itemId}`
      if (priceLevel) {
        key += `_${priceLevel.id}`;
        if (cost) {
          key += `_${cost}`;
          if (quantity) {
            key += `_${quantity}`;
          }
        }
      }

      const cached_key = Object.keys(addonsData).find(k => k.startsWith(key));
      return cached_key ? addonsData[cached_key] : null;
    }),
  })),
  withMethods((store, itemService = inject(ItemsService), eventService = inject(EventService), navigation = inject(NavigationService), toastService = inject(ToastService), posthogService = inject(PostHogService), appStore = inject(AppStore)) => {
      const stopProgressPolling$ = new Subject<void>();

      // Addon requests for different items run in parallel, so the status stays pending until every
      // one of them has settled rather than flipping to fulfilled when the first finishes
      let addonsInFlight = 0;
      let addonsFailure: string | null = null;
      const startAddons = () => {
        if (addonsInFlight++ === 0) {
          addonsFailure = null;
          patchState(store, setPending('addons'));
        }
      };
      const settleAddons = () => {
        if (--addonsInFlight === 0) {
          patchState(store, addonsFailure ? setError('addons', addonsFailure) : setFulfilled('addons'));
        }
      };

      const pollSearchProgress = rxMethod<void>(
        pipe(
          switchMap(() =>
            // small initial delay gives the search POST a head start reaching the
            // backend before we poll, so we don't briefly read a still-stale
            // progress entry from a previous search against the same cart
            timer(400, 1000).pipe(
              switchMap(() => itemService.getSearchProgress().pipe(catchError(() => of(null)))),
              tap((progress) => {
                if (progress) {
                  patchState(store, {search_progress: progress});
                }
              }),
              takeUntil(stopProgressPolling$)
            )
          )
        )
      );

      return {
      setPriceLevel: (level: IPriceLevel | null) => patchState(store, {price_level: level}),
      setQuantity: (quantity: number) => patchState(store, {quantity}),

      search: rxMethod<ISearchParams>(
        pipe(
          tap((params: ISearchParams) => {
            if (!appStore.client_vendors().length) {
              appStore.getClientVendors();
            }

            patchState(store, setPending('search'), {
              search_params: params,
              search_progress: {},
              compare_items: {}
            });
          }),
          switchMap((params) => {
            pollSearchProgress();

            return itemService.search(params).pipe(
              tap((results: IItem[]) => {
                stopProgressPolling$.next();
                eventService.emit("search-performed", {items: results})
                posthogService.posthog.capture('tire_search_performed', {
                  search_params: params,
                  result_count: results.length,
                });
                navigation.navigate('results');
                return patchState(store, setFulfilled('search'), {results})
              }),
              catchError((error) => {
                stopProgressPolling$.next();
                toastService.error(error.message);
                patchState(store, setError('search', error.message));
                posthogService.posthog.capture('tire_search_error', {
                  error_message: error.message,
                });
                return of([]);
              })
            );
          })
        )
      ),

      fetchPricing: rxMethod<ItemPricingRequest>(
        pipe(
          tap(() => patchState(store, setPending('pricing'))),
          debounceTime(500),
          switchMap((request) => {
            // Group requests by item_id and filter out costs we already have
            const consolidatedRequests = request.items.reduce((acc, req) => {
              const key = req.item_id;

              // Filter out costs we already have pricing for
              const costsToFetch = req.costs.filter(cost =>
                cost > 0 && !(`${store.currentPriceLevel()?.id}_${req.item_id}_${cost}` in store.pricing_data())
              );

              // Skip if no costs need fetching
              if (costsToFetch.length === 0) return acc;

              if (!acc[key]) {
                acc[key] = {
                  item_id: req.item_id,
                  brand: req.brand,
                  model: req.model,
                  item_number: req.item_number,
                  costs: costsToFetch,
                  map: req.map
                };
              } else {
                // Merge and deduplicate costs
                acc[key].costs = [...new Set([...acc[key].costs, ...costsToFetch])];
                acc[key].map ??= req.map;
              }
              return acc;
            }, {} as Record<string | number, PricingItem>);

            const itemsToFetch = Object.values(consolidatedRequests);

            if (itemsToFetch.length === 0) {
              patchState(store, setFulfilled('pricing'));
              return of([]);
            }

            return itemService.getPricing({...request, items: itemsToFetch}).pipe(
              tap((response) => {
                const newPricingData = {...store.pricing_data()};

                response.forEach(item => {
                  Object.keys(item.prices).forEach(costKey => {
                    const cost = parseFloat(costKey);
                    newPricingData[`${store.currentPriceLevel()?.id}_${item.item_id}_${cost}`] = {
                      cost: cost,
                      price: parseFloat(item.prices[costKey]['price']),
                      price_special: parseFloat(item.prices[costKey]['price_special'])
                    };
                  });
                });

                patchState(store, setFulfilled('pricing'), {pricing_data: newPricingData});
              }),
              catchError((error) => {
                patchState(store, setError('pricing', error.message));
                return of([]);
              })
            );
          })
        )
      ),
      fetchAddons: rxMethod<AddonRequest[]>(
        pipe(
          tap(startAddons),
          mergeMap((requests) => {
            const uncached = requests.filter(req => {
              const key = `${req.item_id}_${req.price_level_id}_${req.price}_${req.quantity}`;
              return !(key in store.addons_data());
            });

            if (uncached.length === 0) {
              settleAddons();
              return of({});
            }

            const requestMap = uncached.reduce((acc, req) => {
              const key = `${req.item_id}_${req.price_level_id}_${req.price}_${req.quantity}`;
              acc[key] = itemService.getAddonsForItem(req);
              return acc;
            }, {} as Record<string, ReturnType<typeof itemService.getAddonsForItem>>);

            return forkJoin(requestMap).pipe(
              tap((results) => {
                patchState(store, {addons_data: {...store.addons_data(), ...results}});
              }),
              catchError((error) => {
                addonsFailure = error.message;
                return of({});
              }),
              finalize(settleAddons),
            );
          })
        )
      ),


      clearResults: () => patchState(store, setIdle('search'), {results: []}),
      clearPricing: () => patchState(store, setIdle('pricing'), {pricing_data: {}}),
    };
    },
  ),
  withMethods((store, cartItemService = inject(CartItemService), posthogService = inject(PostHogService)) => ({
      addCompareItem: (item: ICompareItem, level?: 'good' | 'better' | 'best') => {
        store.fetchAddons([{
          item_id: item.id,
          brand: item.brand,
          model: item.model,
          item_number: item.item_number,
          price: weightedPrice(item),
          quantity: item.sourcing.reduce((sum, g) => sum + g.selected_quantity, 0),
          price_level_id: String(store.currentPriceLevel()?.id),
        }]);

        const autoSort = !level;
        const current = store.compare_items();

        let currentLevel: 'good' | 'better' | 'best' | undefined;
        if (current.good?.id === item.id) currentLevel = 'good';
        else if (current.better?.id === item.id) currentLevel = 'better';
        else if (current.best?.id === item.id) currentLevel = 'best';

        if (!level) {
          if (!current.good) level = 'good';
          else if (!current.better) level = 'better';
          else level = 'best';
        }

        const updated = {...current};
        if (currentLevel) {
          updated[currentLevel] = current[level];
        }

        const existingNotes = currentLevel ? current[currentLevel]?.customer_notes : undefined;
        updated[level] = {...item, customer_notes: item.customer_notes ?? existingNotes};

        if (autoSort) {
          const sorted = [updated.good, updated.better, updated.best]
            .filter((i): i is ICompareItem => !!i)
            .sort((a, b) => weightedCost(a) > weightedCost(b) ? 1 : -1);

          const levels: ('good' | 'better' | 'best')[] = ['good', 'better', 'best'];
          sorted.forEach((i, idx) => updated[levels[idx]] = i);
          levels.slice(sorted.length).forEach(l => updated[l] = undefined);
        }

        const levels: ('good' | 'better' | 'best')[] = ['good', 'better', 'best'];
        const itemsToSync = levels.filter(l => updated[l]).map(l => ({item: updated[l]!, grade: l}));
        cartItemService.sync(itemsToSync);

        posthogService.posthog.capture('tire_added_to_compare', {
          tire_brand: item.brand,
          tire_model: item.model,
          tire_id: item.id,
          compare_grade: level,
        });

        patchState(store, {compare_items: updated});
      },

      // State-only update for a compare slot (e.g. notes); unlike addCompareItem it doesn't sync or refetch
      updateCompareItem: (level: 'good' | 'better' | 'best', changes: Partial<ICompareItem>) => {
        const current = store.compare_items();
        const item = current[level];
        if (!item) return;
        patchState(store, {compare_items: {...current, [level]: {...item, ...changes}}});
      },

      clearCompare: () => {
        cartItemService.clear();
        patchState(store, {compare_items: {}})
      },

      removeCompareItem: (item: IItem) => {
        cartItemService.destroy(item.id);
        const current = store.compare_items();
        patchState(store, {
          compare_items: {
            good: current.good?.id === item.id ? undefined : current.good,
            better: current.better?.id === item.id ? undefined : current.better,
            best: current.best?.id === item.id ? undefined : current.best
          }
        });
      },
    }),
  )
);
